import { create } from 'zustand';
import { useWalletStore } from './useWalletStore';
import { useAccountStore } from './useAccountStore';

const SESSION_KEY = 'rivellum-console-session';
const SESSION_MAX_AGE_MS = 4 * 60 * 60 * 1000; // 4 hours

interface StoredSession {
  address: string;
  publicKey: string;
  signedAt: number;
  challengeHash: string;
}

export type AuthStatus =
  | 'idle'
  | 'detecting-wallet'
  | 'awaiting-connect'
  | 'awaiting-signature'
  | 'verifying'
  | 'authenticated'
  | 'error';

export interface AuthError {
  code: 'INVITE_REQUIRED' | 'INVALID_INVITE_CODE' | 'WALLET_NOT_FOUND' | 'WALLET_LOCKED' | 'CONNECTION_REJECTED' | 'SIGNATURE_REJECTED' | 'SIGNATURE_INVALID' | 'CHALLENGE_EXPIRED' | 'NO_ACCOUNT' | 'UNKNOWN';
  message: string;
}

const INVITE_SESSION_KEY = 'rivellum-console-invite-approved';
const INVITE_CODES = [
  'RIVELLUM-ACCESS-2026',
];

function normalizeInviteCode(code: string): string {
  return code.trim().toUpperCase();
}

function approveInviteCode(code: string): boolean {
  const normalized = normalizeInviteCode(code);
  if (!normalized) return false;
  if (!INVITE_CODES.includes(normalized)) return false;
  sessionStorage.setItem(INVITE_SESSION_KEY, '1');
  return true;
}

function hasApprovedInviteCode(): boolean {
  return sessionStorage.getItem(INVITE_SESSION_KEY) === '1';
}

interface AuthState {
  status: AuthStatus;
  address: string | null;
  publicKey: string | null;
  error: AuthError | null;

  beginLogin: (inviteCode?: string) => Promise<void>;
  logout: () => void;
  restoreSession: () => void;
  clearError: () => void;
}

function generateChallenge(): string {
  const nonce = crypto.getRandomValues(new Uint8Array(32));
  const hex = Array.from(nonce).map(b => b.toString(16).padStart(2, '0')).join('');
  const timestamp = Date.now();
  return `Rivellum Console sign-in\n\nNonce: ${hex}\nTimestamp: ${timestamp}\nDomain: app.console.rivellum.network`;
}

async function hashChallenge(challenge: string): Promise<string> {
  const encoded = new TextEncoder().encode(challenge);
  const digest = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
}

function loadSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed: StoredSession = JSON.parse(raw);
    if (Date.now() - parsed.signedAt > SESSION_MAX_AGE_MS) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    if (!parsed.address || !parsed.publicKey || !parsed.challengeHash) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  }
}

function saveSession(session: StoredSession): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

export const useAuthStore = create<AuthState>()((set) => ({
  status: 'idle',
  address: null,
  publicKey: null,
  error: null,

  beginLogin: async (inviteCode?: string) => {
    // Always reset to detecting-wallet so the flow can restart after errors.
    set({ status: 'detecting-wallet', error: null, address: null, publicKey: null });

    if (!hasApprovedInviteCode()) {
      const provided = (inviteCode ?? '').trim();
      if (!provided) {
        set({
          status: 'error',
          error: { code: 'INVITE_REQUIRED', message: 'An invite code is required to access Rivellum Console.' },
        });
        return;
      }

      if (!approveInviteCode(provided)) {
        set({
          status: 'error',
          error: { code: 'INVALID_INVITE_CODE', message: 'The invite code is invalid. Please request a valid code.' },
        });
        return;
      }
    }

    const walletStore = useWalletStore.getState();

    // Use the wallet store to detect and connect
    const provider = await walletStore.detectProvider();
    if (!provider?.isNatos) {
      set({
        status: 'error',
        error: { code: 'WALLET_NOT_FOUND', message: 'Natos Wallet extension was not detected. Please install Natos Wallet and reload this page.' },
      });
      return;
    }

    set({ status: 'awaiting-connect' });

    const connected = await walletStore.connect();
    if (!connected) {
      const walletError = useWalletStore.getState().error;
      set({
        status: 'error',
        error: {
          code: 'CONNECTION_REJECTED',
          message: walletError || 'Wallet connection was rejected or the wallet is locked. Please unlock your wallet and try again.',
        },
      });
      return;
    }

    const activeAccount = useWalletStore.getState().activeAccount;
    const activeAddress = activeAccount?.address ?? null;

    if (!activeAddress) {
      set({
        status: 'error',
        error: { code: 'NO_ACCOUNT', message: 'No account found in Natos Wallet. Please create or import an account.' },
      });
      return;
    }

    set({ status: 'awaiting-signature' });

    const challenge = generateChallenge();
    const signedMessage = await walletStore.signMessage(challenge, 'utf8');
    if (!signedMessage) {
      const walletError = useWalletStore.getState().error;
      set({
        status: 'error',
        error: {
          code: 'SIGNATURE_REJECTED',
          message: walletError || 'Sign-in was cancelled. You must sign the authentication challenge to continue.',
        },
      });
      return;
    }

    set({ status: 'verifying' });

    if (!signedMessage.signature || !signedMessage.public_key) {
      set({
        status: 'error',
        error: { code: 'SIGNATURE_INVALID', message: 'Invalid signature received from wallet.' },
      });
      return;
    }

    try {
      const challengeHash = await hashChallenge(challenge);

      const session: StoredSession = {
        address: activeAddress,
        publicKey: signedMessage.public_key,
        signedAt: Date.now(),
        challengeHash,
      };
      saveSession(session);

      set({
        status: 'authenticated',
        address: activeAddress,
        publicKey: signedMessage.public_key,
        error: null,
      });
    } catch {
      set({
        status: 'error',
        error: { code: 'UNKNOWN', message: 'Authentication failed. Please try again.' },
      });
    }
  },

  logout: () => {
    clearSession();
    sessionStorage.removeItem(INVITE_SESSION_KEY);
    useWalletStore.getState().disconnect();
    useAccountStore.getState().clearAll();
    // Lazy import to avoid circular dependency
    import('./useProjectStore').then(m => m.useProjectStore.getState().closeProject());
    set({ status: 'idle', address: null, publicKey: null, error: null });
  },

  restoreSession: () => {
    const session = loadSession();
    if (session) {
      set({
        status: 'authenticated',
        address: session.address,
        publicKey: session.publicKey,
        error: null,
      });
      // Re-establish wallet connection silently in the background
      // Only if the provider reports already connected (no popup)
      const walletStore = useWalletStore.getState();
      walletStore.detectProvider().then(provider => {
        if (provider?.isNatos && typeof provider.isConnected === 'function' && provider.isConnected()) {
          walletStore.connect();
        }
      });
    }
  },

  clearError: () => {
    set({ error: null, status: 'idle' });
  },
}));
